import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { service } from "./service";
import { Breadcrumb } from "@gull";

const buildEmptyTokenModel = () => ({
    idTokenNre: 0,
    idRegistro: 0,
    token: "",
    stato: "",
    descrizione: "",
    dataInizioValidita: null,
    dataFineValidita: null,
    severity: "",
    codEsito: "",
    operazione: "",
    descrizioneToken: "",
    success: false,
    statoToken: null,
    info: {},
    comunicazioni: {},
    errori: []
});

const sanitizeToken = (value) => {
    if (!value) return "";

    return value
        .trim()
        .replace(/['"`]/g, "")
        .replace(/\s+/g, "");
};

const isInvalidDateValue = (value) => {
    if (!value) return true;

    const raw = String(value).trim();

    if (!raw) return true;

    const invalidPatterns = [
        "0001-01-01",
        "0001-1-1",
        "01/01/0001",
        "1/1/0001",
        "1900-01-01",
        "1900-1-1",
        "01/01/1900",
        "1/1/1900"
    ];

    if (invalidPatterns.some((x) => raw.startsWith(x))) {
        return true;
    }

    const date = new Date(raw);

    if (Number.isNaN(date.getTime())) {
        return true;
    }

    const year = date.getFullYear();

    if (year <= 1900) {
        return true;
    }

    return false;
};

const normalizeDateValue = (value) => {
    if (isInvalidDateValue(value)) return null;
    return value;
};

const formatDateTime = (value) => {
    if (isInvalidDateValue(value)) return "-";

    try {
        const date = new Date(value);

        if (Number.isNaN(date.getTime())) return "-";

        return date.toLocaleString("it-IT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
        });
    } catch {
        return "-";
    }
};

const formatText = (value) => {
    if (value === null || value === undefined) return "-";

    const text = String(value).trim();

    return text || "-";
};

const hasTokenSalvato = (model) => {
    return !!sanitizeToken(model?.token || "");
};

const isTokenRevocato = (model) => {
    const stato = Number(model?.stato);

    if (stato === 1) return true;

    const descrizione = String(model?.descrizione || "").toLowerCase();

    if (descrizione.includes("revoc")) return true;

    return false;
};

const isTokenScaduto = (model) => {
    if (!model?.dataFineValidita) return false;

    if (isInvalidDateValue(model.dataFineValidita)) return false;

    const dataFine = new Date(model.dataFineValidita);

    if (Number.isNaN(dataFine.getTime())) return false;

    return dataFine <= new Date();
};

const getReadableState = (model) => {
    if (isTokenRevocato(model)) return "Revocato";

    if (isTokenScaduto(model)) return "Scaduto";

    if (hasTokenSalvato(model)) return "Presente";

    return "-";
};

const extractDataFromResponse = (response) => {
    if (!response) return null;

    if (response.data) return response.data;

    return response;
};

const hasValidRecord = (data) => {
    if (!data) return false;

    if (typeof data !== "object") {
        return false;
    }

    return Object.keys(data).length > 0;
};

const normalizeTokenModelFromRecord = (record, idRegistro) => {
    const data = record || {};

    return {
        idTokenNre: data.idTokenNre ?? data.idtokennre ?? data.id ?? 0,
        idRegistro: data.idRegistro ?? data.idregistro ?? idRegistro ?? 0,

        token: data.token ?? "",

        stato: data.stato ?? "",

        descrizione: data.descrizione ?? data.message ?? "",

        dataInizioValidita: normalizeDateValue(
            data.dataInizioValidita ??
            data.datainiziovalidita ??
            data.dataInizio ??
            null
        ),

        dataFineValidita: normalizeDateValue(
            data.dataFineValidita ??
            data.datafinevalidita ??
            data.dataFine ??
            null
        ),

        severity: data.severity ?? "",
        codEsito: data.codEsito ?? data.codesito ?? "",

        operazione: data.operazione ?? "",
        descrizioneToken: data.descrizioneToken ?? data.descrizionetoken ?? "",

        success: !!data.success,

        statoToken: data.statoToken ?? data.statotoken ?? null,

        info: data.info || {},
        comunicazioni: data.comunicazioni || {},
        errori: data.errori || []
    };
};

const GestioneTokenNreCard = ({
    tokenIniziale = null,
    onSaved = null
}) => {
    const dispatch = useDispatch();

    const [registri, setRegistri] = useState([]);
    const [idRegistroSelezionato, setIdRegistroSelezionato] = useState(0);
    const [cfUtente, setCfUtente] = useState("");

    const [loadingRegistri, setLoadingRegistri] = useState(false);
    const [loadingTokenNre, setLoadingTokenNre] = useState(false);
    const [loadingRichiedi, setLoadingRichiedi] = useState(false);
    const [loadingSalvaVerifica, setLoadingSalvaVerifica] = useState(false);
    const [loadingRevoca, setLoadingRevoca] = useState(false);

    const [model, setModel] = useState(tokenIniziale || buildEmptyTokenModel());
    const [tokenInput, setTokenInput] = useState(tokenIniziale?.token || "");
    const [alert, setAlert] = useState({ type: "", message: "" });

    const tokenPulito = useMemo(() => sanitizeToken(tokenInput), [tokenInput]);

    const tokenPresente = useMemo(() => hasTokenSalvato(model), [model]);
    const tokenRevocato = useMemo(() => isTokenRevocato(model), [model]);
    const tokenScaduto = useMemo(() => isTokenScaduto(model), [model]);

    const tokenInputPresente = useMemo(() => {
        return !!sanitizeToken(tokenInput);
    }, [tokenInput]);

    const canRichiedi = useMemo(() => {
        if (!idRegistroSelezionato) return false;

        if (!tokenPresente) return true;

        if (tokenRevocato) return true;

        if (tokenScaduto) return true;

        return false;
    }, [
        idRegistroSelezionato,
        tokenPresente,
        tokenRevocato,
        tokenScaduto
    ]);

    const canSalvaEVerifica = useMemo(() => {
        if (!idRegistroSelezionato) return false;

        if (!tokenInputPresente) return false;

        if (tokenRevocato) return false;

        if (tokenScaduto) return false;

        return true;
    }, [
        idRegistroSelezionato,
        tokenInputPresente,
        tokenRevocato,
        tokenScaduto
    ]);

    const canRevoca = useMemo(() => {
        if (!idRegistroSelezionato) return false;

        if (!tokenPresente) return false;

        if (tokenRevocato) return false;

        return true;
    }, [
        idRegistroSelezionato,
        tokenPresente,
        tokenRevocato
    ]);

    const messaggioBloccoRichiedi = useMemo(() => {
        if (!idRegistroSelezionato) return "";

        if (tokenPresente && !tokenRevocato && !tokenScaduto) {
            return "È già presente un token. Per richiederne uno nuovo devi prima revocare quello attuale.";
        }

        return "";
    }, [
        idRegistroSelezionato,
        tokenPresente,
        tokenRevocato,
        tokenScaduto
    ]);

    const messaggioBloccoVerifica = useMemo(() => {
        if (!idRegistroSelezionato) return "";

        if (!tokenInputPresente) {
            return "Inserisci un token prima di procedere con la verifica.";
        }

        if (tokenRevocato) {
            return "Il token risulta revocato. Richiedi un nuovo token prima di procedere.";
        }

        if (tokenScaduto) {
            return "Il token risulta scaduto. Richiedi un nuovo token prima di procedere.";
        }

        return "";
    }, [
        idRegistroSelezionato,
        tokenInputPresente,
        tokenRevocato,
        tokenScaduto
    ]);

    useEffect(() => {
        loadRegistri();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showAlert = (type, message) => {
        setAlert({ type, message });
    };

    const clearAlert = () => {
        setAlert({ type: "", message: "" });
    };

    const loadRegistri = async () => {
        setLoadingRegistri(true);

        try {
            const response = await dispatch(service.elencoRegistriIva());
            const data = response || [];

            setRegistri(data);

            if (data.length > 0) {
                const primoRegistro = data[0];
                const idRegistro = primoRegistro.idregistro;

                setIdRegistroSelezionato(idRegistro);
                setCfUtente(primoRegistro.codiceFiscale || "");

                await loadTokenNre(idRegistro, false);
            }
        } catch (error) {
            showAlert("danger", error?.message || "Errore durante il caricamento dei registri IVA.");
        } finally {
            setLoadingRegistri(false);
        }
    };

    const loadTokenNre = async (idRegistro, showError = true) => {
        if (!idRegistro) {
            setModel(buildEmptyTokenModel());
            setTokenInput("");
            return;
        }

        setLoadingTokenNre(true);

        try {
            const response = await dispatch(service.getToken(idRegistro));
            const data = extractDataFromResponse(response);

            if (!hasValidRecord(data)) {
                const emptyModel = {
                    ...buildEmptyTokenModel(),
                    idRegistro
                };

                setModel(emptyModel);
                setTokenInput("");

                return;
            }

            const nextModel = normalizeTokenModelFromRecord(data, idRegistro);

            setModel(nextModel);
            setTokenInput(nextModel.token || "");

            if (onSaved) {
                onSaved(nextModel, response);
            }
        } catch (error) {
            const emptyModel = {
                ...buildEmptyTokenModel(),
                idRegistro
            };

            setModel(emptyModel);
            setTokenInput("");

            if (showError) {
                showAlert("danger", error?.message || "Errore durante il caricamento del token.");
            }
        } finally {
            setLoadingTokenNre(false);
        }
    };

    const mapA2fToViewModel = (response, oldModel) => {
        const data = response?.data || {};

        const token =
            data?.token ??
            response?.token ??
            oldModel.token ??
            "";

        const isSuccess =
            response?.isSuccess === true ||
            data?.success === true ||
            response?.success === true;

        return {
            ...oldModel,

            idRegistro: idRegistroSelezionato,

            token,

            stato:
                data?.stato ??
                response?.stato ??
                oldModel.stato ??
                "",

            descrizione:
                data?.message ||
                response?.message ||
                data?.descrizione ||
                response?.descrizione ||
                oldModel.descrizione ||
                "",

            dataInizioValidita: normalizeDateValue(
                data?.dataInizioValidita ||
                data?.dataInizio ||
                oldModel.dataInizioValidita
            ),

            dataFineValidita: normalizeDateValue(
                data?.dataFineValidita ||
                data?.dataFine ||
                oldModel.dataFineValidita
            ),

            severity:
                data?.severity ||
                response?.severity ||
                "",

            codEsito:
                data?.codEsito ||
                response?.codEsito ||
                "",

            operazione:
                data?.operazione ||
                response?.operazione ||
                "",

            success: isSuccess,

            descrizioneToken:
                data?.descrizioneToken ||
                response?.descrizioneToken ||
                "",

            statoToken:
                data?.statoToken ??
                response?.statoToken ??
                null,

            info: data?.info || {},
            comunicazioni: data?.comunicazioni || {},
            errori: data?.errori || []
        };
    };

    const handleChangeRegistro = async (e) => {
        clearAlert();

        const value = Number(e.target.value || 0);
        const registro = registri.find((x) => x.idregistro === value);

        setIdRegistroSelezionato(value);
        setCfUtente(registro?.codiceFiscale || "");

        await loadTokenNre(value);
    };

    const handleRichiedi = async () => {
        clearAlert();

        if (!idRegistroSelezionato) {
            showAlert("warning", "Seleziona un registro IVA.");
            return;
        }

        if (!canRichiedi) {
            showAlert("warning", "È già presente un token valido. Per richiederne uno nuovo devi prima revocare quello attuale.");
            return;
        }

        setLoadingRichiedi(true);

        try {
            const payload = {
                idRegistro: idRegistroSelezionato,
                cfUtente,
                token: ""
            };

            const response = await dispatch(service.creaIdSessione(payload));
            const nextModel = mapA2fToViewModel(response, model);

            if (response?.isSuccess) {
                showAlert("success", response?.message || "Richiesta token eseguita con successo.");

                await loadTokenNre(idRegistroSelezionato, false);
            } else {
                setModel(nextModel);

                if (nextModel.token) {
                    setTokenInput(nextModel.token);
                }

                showAlert("danger", response?.message || "Errore durante la richiesta del token.");
            }

            if (onSaved) {
                onSaved(nextModel, response);
            }
        } catch (error) {
            showAlert("danger", error?.message || "Errore imprevisto durante la richiesta del token.");
        } finally {
            setLoadingRichiedi(false);
        }
    };

    const handleSalvaEVerifica = async () => {
        clearAlert();

        if (!idRegistroSelezionato) {
            showAlert("warning", "Seleziona un registro IVA.");
            return;
        }

        const token = sanitizeToken(tokenInput);

        if (!token) {
            showAlert("warning", "Incolla un token valido prima di continuare.");
            return;
        }

        if (!canSalvaEVerifica) {
            showAlert("warning", messaggioBloccoVerifica || "Non è possibile verificare questo token.");
            return;
        }

        setLoadingSalvaVerifica(true);

        try {
            const payloadVerifica = {
                idRegistro: idRegistroSelezionato,
                cfUtente,
                token
            };

            const verificaResponse = await dispatch(service.verificaIdSessione(payloadVerifica));

            const nextModel = {
                ...mapA2fToViewModel(verificaResponse, model),
                idRegistro: idRegistroSelezionato,
                token
            };

            if (verificaResponse?.isSuccess) {
                showAlert("success", verificaResponse?.message || "Token verificato correttamente.");

                await loadTokenNre(idRegistroSelezionato, false);
            } else {
                setModel(nextModel);
                setTokenInput(token);

                showAlert("danger", verificaResponse?.message || "Verifica token fallita.");
            }

            if (onSaved) {
                onSaved(nextModel, verificaResponse);
            }
        } catch (error) {
            showAlert("danger", error?.message || "Errore durante il salvataggio/verifica del token.");
        } finally {
            setLoadingSalvaVerifica(false);
        }
    };

    const handleRevoca = async () => {
        clearAlert();

        if (!idRegistroSelezionato) {
            showAlert("warning", "Seleziona un registro IVA.");
            return;
        }

        const token = sanitizeToken(model.token || tokenInput);

        if (!token) {
            showAlert("warning", "Nessun token presente da revocare.");
            return;
        }

        if (!canRevoca) {
            showAlert("warning", "Il token risulta già revocato oppure non è revocabile.");
            return;
        }

        setLoadingRevoca(true);

        try {
            const payload = {
                idRegistro: idRegistroSelezionato,
                cfUtente,
                token
            };

            const response = await dispatch(service.revocaIdSessione(payload));

            const nextModel = {
                ...mapA2fToViewModel(response, model),
                idRegistro: idRegistroSelezionato,
                token
            };

            if (response?.isSuccess) {
                showAlert("success", response?.message || "Token revocato correttamente.");

                await loadTokenNre(idRegistroSelezionato, false);
            } else {
                setModel(nextModel);

                showAlert("danger", response?.message || "Revoca token fallita.");
            }

            if (onSaved) {
                onSaved(nextModel, response);
            }
        } catch (error) {
            showAlert("danger", error?.message || "Errore durante la revoca del token.");
        } finally {
            setLoadingRevoca(false);
        }
    };

    const disabledActions =
        loadingRegistri ||
        loadingTokenNre ||
        !idRegistroSelezionato;

    return (
        <>
            <Breadcrumb
                routeSegments={[
                    { name: "Menù Accettazione", path: "/accettazione/menu" },
                    { name: "Token Nre" },
                ]}
            />

            <div className="card shadow-sm">
                <div className="card-header">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                        <div>
                            <h5 className="mb-0">Gestione token</h5>
                            <div className="text-muted">
                                Seleziona il registro, richiedi il token, oppure incolla quello ricevuto e procedi con la verifica.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label fw-semibold">Registro IVA</label>

                            <select
                                className="form-control"
                                value={idRegistroSelezionato}
                                onChange={handleChangeRegistro}
                                disabled={loadingRegistri || loadingTokenNre}
                            >
                                {registri.length === 0 && (
                                    <option value={0}>
                                        {loadingRegistri ? "Caricamento registri..." : "Nessun registro disponibile"}
                                    </option>
                                )}

                                {registri.map((item) => (
                                    <option key={item.idregistro} value={item.idregistro}>
                                        {item.descrizione}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {loadingTokenNre && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">
                                    Caricamento dati token in corso...
                                </div>
                            </div>
                        )}

                        <div className="col-12">
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <tbody>
                                        <tr>
                                            <th className="w-25">Token salvato</th>
                                            <td className="text-break">
                                                {formatText(model.token)}
                                            </td>
                                        </tr>

                                        <tr>
                                            <th>Stato</th>
                                            <td>{getReadableState(model)}</td>
                                        </tr>

                                        <tr>
                                            <th>Descrizione</th>
                                            <td>{formatText(model.descrizione)}</td>
                                        </tr>

                                        <tr>
                                            <th>Data inizio validità</th>
                                            <td>{formatDateTime(model.dataInizioValidita)}</td>
                                        </tr>

                                        <tr>
                                            <th>Data fine validità</th>
                                            <td>{formatDateTime(model.dataFineValidita)}</td>
                                        </tr>

                                        <tr>
                                            <th>Codice esito</th>
                                            <td>{formatText(model.codEsito)}</td>
                                        </tr>

                                        <tr>
                                            <th>Operazione</th>
                                            <td>{formatText(model.operazione)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="col-12">
                            <label className="form-label fw-semibold">
                                Incolla token ricevuto via email
                            </label>

                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={tokenInput}
                                    onChange={(e) => setTokenInput(e.target.value)}
                                    placeholder="Incolla qui il token"
                                    disabled={loadingTokenNre}
                                />

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setTokenInput(sanitizeToken(tokenInput))}
                                    disabled={loadingTokenNre}
                                >
                                    Pulisci
                                </button>
                            </div>

                            {tokenInput !== tokenPulito && (
                                <div className="mt-2 small text-muted">
                                    Token pulito:{" "}
                                    <span className="text-break">
                                        {tokenPulito}
                                    </span>
                                </div>
                            )}
                        </div>

                        {alert.message && (
                            <div className="col-12">
                                <div className={`alert alert-${alert.type} mb-0`}>
                                    {alert.message}
                                </div>
                            </div>
                        )}

                        <div className="col-12">
                            <div className="d-flex flex-column flex-md-row justify-content-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-primary w-15"
                                    onClick={handleRichiedi}
                                    disabled={disabledActions || loadingRichiedi || !canRichiedi}
                                    title={
                                        !canRichiedi && messaggioBloccoRichiedi
                                            ? messaggioBloccoRichiedi
                                            : ""
                                    }
                                >
                                    {loadingRichiedi ? "Richiesta in corso..." : "Richiedi"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-success text-white w-15"
                                    onClick={handleSalvaEVerifica}
                                    disabled={disabledActions || loadingSalvaVerifica || !canSalvaEVerifica}
                                    title={
                                        !canSalvaEVerifica && messaggioBloccoVerifica
                                            ? messaggioBloccoVerifica
                                            : ""
                                    }
                                >
                                    {loadingSalvaVerifica ? "Salvataggio..." : "Salva e verifica"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger text-white w-15"
                                    onClick={handleRevoca}
                                    disabled={disabledActions || loadingRevoca || !canRevoca}
                                >
                                    {loadingRevoca ? "Revoca in corso..." : "Revoca"}
                                </button>
                            </div>

                            {messaggioBloccoRichiedi && (
                                <div className="text-muted small text-end mt-2">
                                    {messaggioBloccoRichiedi}
                                </div>
                            )}
                        </div>

                        {model.errori?.length > 0 && (
                            <div className="col-12">
                                <div className="mt-2">
                                    <div className="fw-semibold mb-2">
                                        Dettaglio errori
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Codice</th>
                                                    <th>Descrizione</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {model.errori.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.tipoErrore || "-"}</td>
                                                        <td>{item.codEsito || "-"}</td>
                                                        <td>{item.descrEsito || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default GestioneTokenNreCard;